/**
 * <LiveLobbyPreview> — l'image de l'élève en grand, avant d'entrer, et ses deux bascules (organism).
 *
 * Ce qui fait d'un écran une salle d'attente de visio : on se voit, en grand, et on règle son
 * micro et sa caméra SUR l'image, avant d'entrer. Le cadre est `<CameraPreviewFrame>` (la vidéo
 * coupée, l'image noire, la caméra qui ne s'ouvre pas) ; par-dessus, en bas au milieu, les deux
 * boutons ronds du live — les faces de `<LiveControlBar>`, pour que le geste soit le même avant et
 * pendant : au repos la surface douce, coupé la tinte d'erreur et le glyphe barré. Un appareil
 * en défaut (accès refusé) porte le point sur son bouton.
 *
 * Deux marques discrètes, sur l'image : le niveau du micro en bas à gauche (`micLevel`, les barres
 * qui bougent quand l'élève parle) et une connexion faible en haut à droite (`network`). Rien
 * d'autre ne se pose sur l'image : ce qui demande un geste se lit dessous, dans la page.
 *
 * `onToggleMic` absent : pas de bouton du micro.
 *
 * Composes DS pieces only — `<CameraPreviewFrame>`, `<IconButton>`, `<BadgedIconButton>`,
 * `<LevelBars>`, `<Badge>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Icon } from "../../atoms/icons/Icon"
import { LevelBars } from "../../atoms/LevelBars"
import { Text } from "../../atoms/typography/Text"
import { BadgedIconButton } from "../../molecules/BadgedIconButton"
import { CameraPreviewFrame } from "../../molecules/CameraPreviewFrame"
import {
    LIVE_CONTROL_ICON_SIZE,
    LIVE_CONTROL_SIZE,
    LIVE_DEVICE_FACES,
    LIVE_DEVICE_LABELS,
} from "../../molecules/LiveControlBar/LiveControlBar"

export type LiveLobbyPreviewProps = {
    /** L'image de la caméra — le flux, que l'écran monte. */
    children?: React.ReactNode
    /** `landscape` (16:9, le grand écran) ou `portrait` (3:4, le téléphone). Défaut `landscape`. */
    orientation?: "landscape" | "portrait"
    cameraOn: boolean
    onToggleCamera: () => void
    /** La caméra est refusée ou ne s'ouvre pas : le point sur son bouton. Défaut `false`. */
    cameraProblem?: boolean
    /** Le micro. Absent : pas de bouton. */
    micOn?: boolean
    onToggleMic?: () => void
    /** Le micro est refusé : le point sur son bouton. Défaut `false`. */
    micProblem?: boolean
    /** Le niveau du micro, de 0 à 1, dès que l'accès est donné. Absent : pas de barres. */
    micLevel?: number
    /** L'image est toute noire : quelque chose cache l'objectif. */
    obstructed?: boolean
    /** La caméra demandée ne s'ouvre pas : pourquoi, en haut de l'image. */
    deviceError?: string
    /** Une connexion faible : ce qui se lit en haut à droite (« Connexion un peu lente »). */
    network?: string
    /**
     * La caméra n'est pas encore ouverte (le test en est à une autre étape) : ce que l'image dit à la
     * place, au milieu. Ignoré quand le flux est là, ou la vidéo coupée.
     */
    waitingText?: string
    testID?: string
}

const TEXTS = {
    ...LIVE_DEVICE_LABELS,
    micLevel: "Le niveau de ton micro",
}

/** Les boutons du live (`<LiveControlBar>`) : le même geste, la même face, avant et pendant. */
const TOGGLE_SIZE = LIVE_CONTROL_SIZE
const TOGGLE_ICON = LIVE_CONTROL_ICON_SIZE

export function LiveLobbyPreview(props: LiveLobbyPreviewProps) {
    const {
        children,
        orientation = "landscape",
        cameraOn,
        onToggleCamera,
        cameraProblem = false,
        micOn,
        onToggleMic,
        micProblem = false,
        micLevel,
        obstructed = false,
        deviceError,
        network,
        waitingText,
        testID,
    } = props
    const micShown = onToggleMic != null && micOn != null
    const waiting = cameraOn && children == null && waitingText != null && waitingText !== ""

    return (
        <View style={styles.root} testID={testID}>
            <CameraPreviewFrame
                orientation={orientation}
                videoOff={!cameraOn}
                onEnableVideo={onToggleCamera}
                obstructed={obstructed}
                deviceError={deviceError}
            >
                {children}
            </CameraPreviewFrame>

            {waiting && (
                <View style={styles.waiting} pointerEvents="none">
                    <Icon name="video" size={24} color="text-on-night" />
                    <Text size="text-sm" color="text-on-night" center>
                        {waitingText}
                    </Text>
                </View>
            )}

            {network != null && network !== "" && (
                <View style={styles.topRight} pointerEvents="none">
                    <Badge text={network} variant="warning" tinted iconName="wifi" />
                </View>
            )}

            {micLevel != null && micOn !== false && (
                <View style={styles.level} pointerEvents="none">
                    <LevelBars level={micLevel} count={5} accessibilityLabel={TEXTS.micLevel} />
                </View>
            )}

            <View style={styles.toggles}>
                {micShown && (
                    <BadgedIconButton
                        iconName={micOn ? "microphone" : "microphone-slash"}
                        {...(micOn ? LIVE_DEVICE_FACES.on : LIVE_DEVICE_FACES.off)}
                        size={TOGGLE_SIZE}
                        iconSize={TOGGLE_ICON}
                        dot={micProblem}
                        dotLabel={TEXTS.blocked}
                        onPress={onToggleMic}
                        accessibilityLabel={micOn ? TEXTS.micOn : TEXTS.micOff}
                    />
                )}
                <BadgedIconButton
                    iconName={cameraOn ? "video" : "video-slash"}
                    {...(cameraOn ? LIVE_DEVICE_FACES.on : LIVE_DEVICE_FACES.off)}
                    size={TOGGLE_SIZE}
                    iconSize={TOGGLE_ICON}
                    dot={cameraProblem}
                    dotLabel={TEXTS.blocked}
                    onPress={onToggleCamera}
                    accessibilityLabel={cameraOn ? TEXTS.cameraOn : TEXTS.cameraOff}
                />
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        width: "100%",
    },
    toggles: {
        position: "absolute",
        bottom: theme.spacing["4"],
        alignSelf: "center",
        flexDirection: "row",
        gap: theme.spacing["4"],
    },
    waiting: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing["2"],
        padding: theme.spacing["6"],
    },
    topRight: {
        position: "absolute",
        top: theme.spacing["3"],
        right: theme.spacing["3"],
    },
    /** Un voile sous les barres : elles se lisent sur n'importe quelle image. */
    level: {
        position: "absolute",
        bottom: theme.spacing["4"],
        left: theme.spacing["4"],
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["2"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.backdrop,
    },
}))
